import React, { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Loader } from '../core';
import { useDocumentEditorScript } from './hooks/use-document-editor-script';
import { useDocumentEditorConfig } from './hooks/use-document-editor-config';
import { getLang } from 'shared/store/selectors/lang.selector';
import Logger from 'shared/utils/Logger';
import { EVENTS } from 'shared/constants';
import { getFileExtension } from 'shared/utils';
import analyticsService from '../../helpers/analytics.service';
import { DOCUMENT_EDITOR_MODES } from 'shared/constants/document-editor.constants';
import styles from './document-editor.module.scss';

const logger = Logger.create({ loggerName: 'DocumentEditor' });

const EDITOR_CONTAINER_ID = 'document-editor-container';

/**
 * Document Editor component for viewing and editing spreadsheets
 * Supports .xls, .xlsx, .csv, .ods files
 * Note: Document Editor is only supported in US region. For unsupported regions,
 * the component will return null and the parent should render fallback UI.
 *
 * @param {object} props
 * @param {string} [props.itemId] - The knowledge item ID; when absent, the config is fetched by the file link
 * @param {string} [props.title='Document'] - Document title to display
 * @param {'view' | 'edit'} [props.mode='view'] - Editor mode
 * @param {() => void} [props.onDocumentReady] - Callback when document is ready
 * @param {(error: Error) => void} [props.onError] - Callback when an error occurs
 * @param {() => void} [props.onSave] - Callback when document is saved
 * @param {(isModified: boolean) => void} [props.onDocumentModified] - Callback when document modified state changes
 * @param {(actions: { destroy: () => void }) => void} [props.onEditorReady] - Callback when editor is initialized, exposes editor control actions
 * @param {string} [props.link] - File URL; also used to fetch the config when there is no itemId, and for analytics tracking
 * @param {string} [props.className] - Additional CSS class name
 * @param {React.ReactNode} [props.fallback] - Fallback UI to render when Document Editor is not supported
 */
export function DocumentEditor({
  itemId,
  title = 'Document',
  mode = DOCUMENT_EDITOR_MODES.VIEW,
  link,
  onDocumentReady,
  onError,
  onSave,
  onDocumentModified,
  onEditorReady,
  className,
  fallback = null,
}) {
  const editorRef = useRef(null);
  const lang = useSelector(getLang('DOCUMENT_EDITOR'));

  // Load Document Editor API script
  const { loaded: scriptLoaded, error: scriptError, isSupported } = useDocumentEditorScript();

  // Fetch editor configuration from backend
  const {
    config,
    loading: configLoading,
    error: configError,
  } = useDocumentEditorConfig({ itemId, link }, mode);

  // Combined loading state - not loading if feature is not supported
  const isLoading = isSupported && (!scriptLoaded || configLoading);

  // Combined error state
  const error = scriptError || configError;

  // Initialize the editor once script is loaded and config is available
  const initializeEditor = useCallback(() => {
    if (!window.DocsAPI || !config || !isSupported) {
      return;
    }

    // Destroy existing editor instance if any
    if (editorRef.current?.destroyEditor) {
      editorRef.current.destroyEditor();
      editorRef.current = null;
    }

    try {
      editorRef.current = new window.DocsAPI.DocEditor(EDITOR_CONTAINER_ID, {
        ...config,
        events: {
          onDocumentReady: () => {
            onDocumentReady?.();
          },
          onError: event => {
            logger.error('Editor error:', event);
            const errorMessage =
              typeof event.data === 'string' ? event.data : 'An error occurred in the editor';
            onError?.(new Error(errorMessage));
          },
          onWarning: event => {
            logger.warn('Editor warning:', event);
          },
          onInfo: event => {
            // Info events can be logged if needed for debugging
            logger.info('Editor info:', event);
          },
          onDocumentStateChange: event => {
            // event.data is true when document has unsaved changes
            onDocumentModified?.(event.data);
          },
        },
      });

      // Expose editor control actions to parent
      onEditorReady?.({
        destroy: () => {
          if (editorRef.current?.destroyEditor) {
            editorRef.current.destroyEditor();
            editorRef.current = null;
          }
        },
      });
    } catch (err) {
      logger.error('Failed to initialize editor:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize editor';
      onError?.(new Error(errorMessage));
    }
  }, [config, onDocumentReady, onError, onDocumentModified, onEditorReady, isSupported]);

  // Initialize editor when dependencies are ready, and re-initialize when a
  // new config arrives (e.g. the item id or link changed) - initializeEditor
  // destroys the previous instance first
  const initializedConfigRef = useRef(null);
  useEffect(() => {
    if (isSupported && scriptLoaded && config && initializedConfigRef.current !== config) {
      initializedConfigRef.current = config;
      initializeEditor();
    }
  }, [isSupported, scriptLoaded, config, initializeEditor]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (editorRef.current?.destroyEditor) {
        editorRef.current.destroyEditor();
        editorRef.current = null;
      }
      onEditorReady?.(null);
    };
  }, [onEditorReady]);

  // Track analytics when editor opens
  useEffect(() => {
    if (isSupported && config) {
      analyticsService.track(EVENTS.EDITOR.OPEN, {
        is_new_editor: true,
        file_extension: getFileExtension(link),
        mode,
        id: itemId,
        title,
      });
    }
  }, [isSupported, config, link, mode, itemId, title]);

  // Handle errors
  useEffect(() => {
    if (error && isSupported) {
      onError?.(error);
    }
  }, [error, onError, isSupported]);

  // If Document Editor is not supported, render fallback
  if (!isSupported) {
    return fallback;
  }

  // Render error state - prefer the fallback so the user can still reach the file
  if (error) {
    if (fallback) {
      return fallback;
    }

    return (
      <div className={`${styles.container} ${className || ''}`}>
        <div className={styles.errorContainer}>
          <h3 className={styles.errorTitle}>{lang.ERROR_TITLE}</h3>
          <p className={styles.errorMessage}>{error.message}</p>
          <p className={styles.errorHint}>{lang.ERROR_HINT}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${className || ''}`}>
      {isLoading && (
        <div className={styles.loadingOverlay}>
          <Loader aria-hidden="true" />
          <p className={styles.loadingText}>{lang.LOADING.replace('{title}', title)}</p>
        </div>
      )}
      <div
        id={EDITOR_CONTAINER_ID}
        className={`${styles.editorWrapper} ${isLoading ? styles.hidden : ''}`}
        aria-label={title}
      />
    </div>
  );
}
